import { Button } from "@/components/ui/button";
import { Calendar, X, SkipForward, Move } from "lucide-react";
import { Task } from "@/types/task";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface TaskActionsProps {
  task: Task;
  onDeleteClick: (task: Task) => void;
  onCalendarClick: (task: Task) => void;
  onSkipClick?: (task: Task) => void;
  onMoveClick?: (task: Task, importance: number, urgency: number) => void;
  showLabels?: boolean;
}

const TaskActions = ({
  task,
  onDeleteClick,
  onCalendarClick,
  onSkipClick,
  onMoveClick,
}: TaskActionsProps) => {
  const quadrants = [
    { importance: 1, urgency: 1, label: "Do first" },
    { importance: 1, urgency: 0, label: "Schedule" },
    { importance: 0, urgency: 1, label: "Delegate" },
    { importance: 0, urgency: 0, label: "Eliminate" },
  ];

  const currentQuadrant = quadrants.find(
    (q) => q.importance === task.importance && q.urgency === task.urgency
  );

  return (
    <div className="flex gap-1">
      {onMoveClick && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => e.stopPropagation()}
              title="Move to quadrant"
              aria-label="Move to quadrant"
              className="h-8"
            >
              <Move className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel>Move to</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {quadrants.map((q) => (
              <DropdownMenuItem
                key={`${q.importance}-${q.urgency}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveClick(task, q.importance, q.urgency);
                }}
                disabled={
                  q.importance === task.importance && q.urgency === task.urgency
                }
              >
                {q.label}
                {currentQuadrant && q.importance === currentQuadrant.importance && q.urgency === currentQuadrant.urgency && " (current)"}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onCalendarClick(task);
        }}
        title="Add to Google Calendar"
        aria-label="Add to Google Calendar"
        className="h-8"
      >
        <Calendar className="h-4 w-4" />
      </Button>
      {task.is_recurring && !task.completed && onSkipClick && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onSkipClick(task);
          }}
          title="Skip this occurrence"
          aria-label="Skip this occurrence"
          className="h-8 text-indigo-600 hover:text-indigo-700"
        >
          <SkipForward className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onDeleteClick(task);
        }}
        title="Delete task"
        aria-label="Delete task"
        className="h-8"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default TaskActions;
